import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { RaadLogo } from '@/components/RaadLogo';
import { useAuth } from '@/features/auth/useAuth';
import { StaffPanel } from './StaffPanel';
import { LiveSocketProvider } from '@/features/live/LiveSocketProvider';

/** Full-screen splash shown while the silent session bootstrap resolves, so the
 * login screen never flashes for an already-authenticated user. */
function Splash() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-raad-bg text-raad-muted">
      <RaadLogo size={48} title={t('brand.name')} />
      <p className="text-sm">{t('app.loading')}</p>
    </div>
  );
}

/**
 * Gate for protected routes. Waits out the boot bootstrap, then either renders
 * the app or redirects to /login preserving the intended path so the user lands
 * where they were headed after signing in.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') return <Splash />;
  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  // A staff principal on the customer origin: no fleet exists for them, so every
  // protected route resolves to the in-place panel rather than a wall of 403s.
  // Placed here (not per-screen) so it covers every protected route at once and
  // keeps sign-out reachable (17i).
  if (state.status === 'staff') return <StaffPanel />;
  // 56g — the ONE app-level live socket is mounted HERE, inside the authenticated
  // branch and above the routes, so it is a single connection for the whole
  // session (Overview + Live map + vehicle detail all read its status through
  // context) and it never opens for a logged-out visitor, the staff panel above,
  // or the public share page (which lives outside RequireAuth entirely).
  return <LiveSocketProvider>{children}</LiveSocketProvider>;
}
